import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Tables } from "types/supabase";
import { apiRequest, apiGetRequest } from "utils/api";

export enum ChecklistStatus {
  Pending = "Pending",
  Reviewed = "Reviewed",
  Approved = "Approved",
  Rejected = "Rejected",
}

interface BaseLoanItem extends Omit<Tables<"loan_items">, "checklist_item"> {
  checklist_item?: number | null;
  assignments?: string[];
  documents?: any[];
  loan: string | null;
  loan_item_status?: any[];
  contacts?: any[];
}

interface LoanItem extends Omit<BaseLoanItem, "checklist_item"> {
  checklist_item?: {
    id: number;
    assignments?: string[];
    [key: string]: any;
  } | null;
}

export function getLastStatus(
  statuses: Tables<"loan_item_status">[]
): Tables<"loan_item_status"> | null {
  if (!statuses?.length) return null;
  let max = statuses[0];
  for (let i = 1; i < statuses.length; i++) {
    if (statuses[i].id > max.id) {
      max = statuses[i];
    }
  }
  return max || null;
}

export function useChecklist({ loan_id }: { loan_id?: string }) {
  const queryClient = useQueryClient();

  const {
    data: loan_items = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["loan_items", loan_id],
    queryFn: async () => {
      if (!loan_id) return [];

      try {
        const response = await apiRequest<{ loan_items: any[] }>("/supabase/loan_items/select", {
          loan_id,
        });

        // The server returns { loan_items: [...] }
        let items = [];
        if (response && response.loan_items && Array.isArray(response.loan_items)) {
          items = response.loan_items;
        }

        return items;
      } catch (error) {
        console.error("Error fetching loan items:", error);
        throw error;
      }
    },
    enabled: !!loan_id,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["loan_items", loan_id] });

  const categories = [
    ...new Set(loan_items?.map((item: any) => item.category).filter(Boolean) ?? []),
  ].sort((a, b) => a.localeCompare(b));

  const statuses = [
    ChecklistStatus.Pending,
    ChecklistStatus.Reviewed,
    ChecklistStatus.Approved,
    ChecklistStatus.Rejected,
  ];

  return {
    loan_items,
    invalidate,
    isLoading,
    categories,
    statuses,
  };
}

export function useChecklistSettings({ loan_id }: { loan_id?: string }) {
  const queryClient = useQueryClient();

  const { data: loan_items = [], isLoading } = useQuery({
    queryKey: ["loan_items_settings", loan_id],
    queryFn: async () => {
      if (!loan_id) return [];

      try {
        const response = await apiRequest<{ loan_items: any[] }>("/supabase/loan_items/select", {
          loan_id,
        });

        let items = [];
        if (response && response.loan_items && Array.isArray(response.loan_items)) {
          items = response.loan_items;
        }

        return items;
      } catch (error) {
        console.error("Error fetching loan items for settings:", error);
        throw error;
      }
    },
    enabled: !!loan_id,
  });

  const { data: checklistItems = [] } = useQuery({
    queryKey: ["checklist_items_settings"],
    queryFn: async (): Promise<Tables<"checklist_items">[]> => {
      const { checklist_items = [] } = await apiGetRequest<{
        checklist_items: Tables<"checklist_items">[];
      }>("/supabase/checklist_items/select");
      return checklist_items;
    },
    staleTime: 1000 * 60 * 5,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["loan_items_settings", loan_id],
    });

  const categories = [
    ...new Set([
      ...(loan_items?.map((item: any) => item.category) ?? []),
      ...(checklistItems?.map((item: any) => item.category) ?? []),
    ]),
  ]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  return {
    loan_items,
    invalidate,
    isLoading,
    categories,
  };
}
